import { type CSSProperties, type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, LockKeyhole } from 'lucide-react'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'

/**
 * Frosted-glass lock screen over a partner-branded preview site.
 *
 * A partner with an `accessCode` in the registry (e.g. garvey) gets the whole
 * site blurred behind a small card until the code is entered. This is a
 * courtesy screen, not security: it exists so a prospective partner's
 * branding isn't publicly browsable before they've agreed to it, while still
 * letting us send them a link. The code also unlocks via `?code=<code>` so an
 * outreach email can carry a single clickable link.
 *
 * Unlock persists in localStorage per partner, so Lee and the partner only
 * type it once per browser.
 */

const unlockKey = (slug: string) => `sd_partner_unlocked_${slug}`

const codesMatch = (input: string, code: string) =>
  input.trim().toLowerCase() === code.trim().toLowerCase()

export default function PartnerGate({ children }: { children: ReactNode }) {
  const partner = COMMUNITY_PARTNER
  const code = partner?.accessCode

  const [locked, setLocked] = useState<boolean>(() => {
    if (!partner || !code) return false
    try {
      if (window.localStorage.getItem(unlockKey(partner.slug)) === '1') return false
    } catch {
      /* ignore */
    }
    // A ?code= in the link unlocks directly (outreach emails carry one link).
    const fromUrl = new URLSearchParams(window.location.search).get('code')
    if (fromUrl && codesMatch(fromUrl, code)) {
      try {
        window.localStorage.setItem(unlockKey(partner.slug), '1')
      } catch {
        /* ignore */
      }
      return false
    }
    return true
  })
  const [value, setValue] = useState('')
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // No scrolling the blurred site behind the lock screen.
  useEffect(() => {
    if (!locked) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [locked])

  if (!partner || !code) return <>{children}</>

  const { colors } = partner

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (codesMatch(value, code)) {
      try {
        window.localStorage.setItem(unlockKey(partner.slug), '1')
      } catch {
        /* ignore */
      }
      setLocked(false)
    } else {
      setError(true)
      setAttempt(a => a + 1)
      inputRef.current?.select()
    }
  }

  return (
    <>
      <div
        aria-hidden={locked || undefined}
        // inert (not just pointer-events) so the blurred site is also
        // unreachable by keyboard and screen readers while locked.
        inert={locked || undefined}
        className={`min-h-full ${locked ? 'pointer-events-none select-none' : ''}`}
        style={{
          filter: locked ? 'blur(16px) saturate(1.15)' : 'blur(0px)',
          transform: locked ? 'scale(1.02)' : 'scale(1)',
          transition: 'filter 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {children}
      </div>

      <AnimatePresence>
        {locked && (
          <motion.div
            key="partner-gate"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.55, ease: 'easeOut' } }}
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-6"
            style={{
              background: `linear-gradient(165deg, ${colors.primary}59 0%, ${colors.deep}73 100%)`,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.985, transition: { duration: 0.35, ease: 'easeIn' } }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-md rounded-3xl border border-white/60 bg-white/85 p-8 shadow-2xl shadow-slate-900/25 backdrop-blur-2xl sm:p-10"
            >
              <div className="flex items-center gap-4">
                <img
                  src={partner.logoUrl}
                  alt={partner.logoAlt}
                  className="h-14 w-14 shrink-0 object-contain"
                />
                <div>
                  <p className="text-base font-semibold text-slate-900">GranGuard</p>
                  <p className="text-base leading-snug text-slate-500">
                    {/* "for the Garvey Center" reads right; "for the The
                        Beekeeper 2" does not, so a name that already starts
                        with an article drops ours. */}
                    {/^the\s/i.test(partner.name) ? `for ${partner.name}` : `for the ${partner.name}`}
                  </p>
                </div>
              </div>

              <h1 className="mt-7 text-2xl font-semibold tracking-tight text-slate-900">
                A private preview
              </h1>
              <p className="mt-2.5 text-base leading-relaxed text-slate-600">
                Enter your access code to continue.
              </p>

              <motion.form
                onSubmit={submit}
                className="mt-6"
                animate={error ? { x: [0, -9, 9, -6, 6, -3, 0] } : { x: 0 }}
                transition={{ duration: 0.4 }}
                key={attempt}
              >
                <label htmlFor="partner-gate-code" className="sr-only">
                  Access code
                </label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={inputRef}
                    id="partner-gate-code"
                    type="text"
                    value={value}
                    onChange={e => {
                      setValue(e.target.value)
                      setError(false)
                    }}
                    placeholder="Access code"
                    autoFocus
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-invalid={error || undefined}
                    aria-describedby={error ? 'partner-gate-error' : undefined}
                    className={`w-full rounded-2xl border bg-white/90 py-3.5 pl-12 pr-4 text-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
                      error
                        ? 'border-red-300 focus:ring-red-300'
                        : 'border-slate-200 focus:border-transparent'
                    }`}
                    style={error ? undefined : ({ '--tw-ring-color': colors.accent } as CSSProperties)}
                  />
                </div>
                {error && (
                  <p id="partner-gate-error" role="alert" className="mt-2.5 text-base text-red-600">
                    That code doesn’t match. Check the note or email from GranGuard.
                  </p>
                )}
                <button
                  type="submit"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-lg font-semibold text-white transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ background: colors.primary, '--tw-ring-color': colors.accent } as CSSProperties}
                >
                  View the preview
                  <ArrowRight className="h-5 w-5" />
                </button>
              </motion.form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
